import React, { Fragment } from 'react';
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux';
import { fullDateShort, fullDateRangeShort } from '@showclix/sc-intl/dist/dateTimeFormatters';
import { ISOStringToDate } from '../../../../helpers/time';
import { isValidTimezoneString } from '../../../../../../shared/util/dates';
import { createLoadingSelector } from '../../../selectors/helpers';
import { getTotalSelectedItemsFromState, getProductErrorsFromState, getBundleErrorsFromState } from '../../ticket-picker/selectors/selected-items';
import { getTotalSelectedProductsFromState } from '../../product-picker/selectors/products';
import { setDisplayConfigurationError, setPriceLevel } from '../../ticket-picker/actions/ticket-picker';
import Event from './Event';
import Button from '@s/Button';
import UpsellInventoryPicker from './UpsellInventoryPicker';
import InventoryConfigurationErrors from '../../ticket-picker/components/InventoryConfigurationErrors';
import { isKioskMode } from '../../../selectors/ui';
import { getSubState } from '../../../reducers/subReducerHelpers';
import { updateQuantities } from '../../../actions/reservation';
import { countCartedItemsForUpsell, getEventUpsellLoadingKey } from '../selectors/upsells';
import UpsellDateTimePicker from './UpsellDateTimePicker';
import { changeDateInternal, getEventSeriesChildListing, setSelectedDate } from '../../series/actions/event-series';
import ChangeDate from '../../ticket-picker/components/ChangeDate';
import { formatDisplayDateForChangeDateButton } from '../../ticket-picker/containers/ChangeDateContainer';
import { clearChildEventDetails } from '../../../actions/event';
import CartMatchOffer from './CartMatchOffer';
import CartMatchPrice from './CartMatchPrice';
import { ITEM_TYPE_PRICE_LEVEL } from '../reducers/upsells';
import { checkCartMatch } from '../actions/upsells';
import KioskableComponent from '../../kiosk/KioskableComponent';
import AddToOrderButton from './AddToOrderButton';
import AddedToCartMessage from './AddedToCartMessage';
import Translation from '@s/Translation';
import { getFetchedEventBundles, getFetchedEventProducts } from '../../../selectors/event';
import { getEventDate } from "@h/helpers";

const IMAGE_SIZE_LARGE = 'large';

const getLoadingKeyFromProps = props => {
    return getEventUpsellLoadingKey(props.upsellId, props?.event?.event_id);
}

/**
 * This wraps the upsell components for a single upsell event map
 *
 * Regarding cart matching:
 * For single event upsells, the cart match will be returned when we fetch the upsell data and get passed in via props
 * For series event upsells, we need to wait until a timeslot is selected to check for cart matching.  So, this component
 * sends an extra request for series events to check for cart matching and stores the result in local state as childEventCartMatch
 *
 * TODO translate
 */
class EventUpsell extends KioskableComponent {

    state = {
        pickerOpen: false,
        loadingChildEvent: false,
        showChildEventPicker: false,
        childEventCartMatch: null,
        showCartMatch: true,
    };

    componentDidMount() {
        if (this.hasValidTimeslotMatch()) {
            if (!this.hasSelectedChildEvent() || +this.props.childEvent.event_id !== +this.props.timeslotMatch.event_id) {
                this.loadChildEvent(this.props.timeslotMatch.event_id);
            } else {
               this.setState({showChildEventPicker: true});
            }
        }
    }

    /**
     * Get the venue text
     */
    getVenue = () => {
        if (this.props.event.hide_venue){
          return '';
        }

        if (!this.props.venueName) {
            return '';
        }

        return this.props.venueCity ? `${this.props.venueName} in ${this.props.venueCity}` : this.props.venueName;
    }

    /**
     * Submit chosen inventory quantities to server if no errors
     */
    submitPicker = () => {
        let productsValid = Object.keys(this.getProductsWithErrors()).length === 0;
        let bundlesValid = Object.keys(this.getBundleProductsWithErrors()).length === 0;
        if (!productsValid || !bundlesValid) {
            this.props.setDisplayConfigurationError(true, this.props.reducerKey);
            return false;
        } else {
            this.props.setDisplayConfigurationError(false, this.props.reducerKey);
        }
        this.props.updateQuantities(this.props.reducerKey, this.props.upsellId, null, getLoadingKeyFromProps(this.props));
    }

    /**
     * Apply the cart match quantities to inventory pickers
     */
    setCartMatchOfferQuantitiesInInventory = () => {
        this.getRelevantCartMatchDetails().forEach(detail => {
            if (CartMatchOffer.isValidDetail(detail)) {
                if (detail.type === ITEM_TYPE_PRICE_LEVEL) {
                   this.props.setPriceLevel(detail.id, detail.count, this.props.reducerKey);
                }
            }
        })
    }

    /**
     * Update reservation to add cart match quantities to cart
     */
    acceptCartMatchOffer = () => {
        const options = {
            ticketQuantities: {},
            productQuantities: {},
            bundleQuantities: {},
        };
        this.getRelevantCartMatchDetails().forEach(detail => {
            if (CartMatchOffer.isValidDetail(detail)) {
                if (detail.type === ITEM_TYPE_PRICE_LEVEL) {
                    options.ticketQuantities[detail.id] = detail.count;
                }
            }
        });
        this.props.updateQuantities(this.props.reducerKey, this.props.upsellId, options, getLoadingKeyFromProps(this.props));
    }

    /**
     * Apply cart match quantities and open picker
     */
    changeTicketsFromCartMatchOffer = () => {
        this.setCartMatchOfferQuantitiesInInventory();
        this.setState({showCartMatch: false, pickerOpen: true});
    }

    /**
     * Do we have a valid cart match offer in props
     */
    hasValidCartMatchDetails = () => {
        return this.cartMatchDetailsValid(this.props.cartMatch);
    }

    /**
     * Validate the provided cart match details
     */
    cartMatchDetailsValid = details => {
        if (!Array.isArray(details) || !details.length) {
            return false;
        }
        let isValid = false;
        details.forEach(detail => {
            if (CartMatchOffer.isValidDetail(detail)) {
                isValid = true;
            }
        });
        return isValid;
    }

    /**
     * Is there a cart match on the selected series child in this.state
     */
    hasSeriesChildCartMatch = () => {
        return this.isSeries() && this.hasSelectedChildEvent() && this.cartMatchDetailsValid(this.state.childEventCartMatch);
    }

    /**
     * Get the cart match details from state or props depending on where they should be
     */
    getRelevantCartMatchDetails = () => {
        if (this.isSeries()) {
            if (this.props.doesTimeslotMatching && this.matchedTimeslotSelected()) {
                return this.props.cartMatch;
            }
            return this.state.childEventCartMatch;
        }
        return this.props.cartMatch;
    }

    /**
     * Determine if we should show a cart match
     */
    shouldShowCartMatch = () => {
        if (!this.state.showCartMatch || !this.props.doesCartMatching) {
            return false;
        }
        if (!this.isSeries()) {
            return this.hasValidCartMatchDetails();
        }
        if (this.isSeries()) {
            if (this.props.doesTimeslotMatching && this.matchedTimeslotSelected()) {
                return this.hasValidCartMatchDetails();
            }
            return this.hasSeriesChildCartMatch();
        }
    }

    /**
     * Should the UI just be the get tickets button
     */
    shouldShowGetTicketsButton = () => {
        return this.props.countCartedItems < 1 && !this.shouldShowCartMatch() && !this.state.pickerOpen;
    }

    hasCartedItems = () => {
        return this.props.countCartedItems > 0;
    }

    /**
     * Is a case in getUI function true?
     */
    shouldGetUI = () => {
        return (this.shouldShowGetTicketsButton() || this.hasCartedItems() || this.shouldShowCartMatch());
    }

    /**
     * Logic for upsell UI flow
     */
    getUI = () => {

        if (this.shouldShowGetTicketsButton()) {
            return (
                <div className="upsell-embedded__action">
                    <Button className="button--primary" onClick={() => {this.setState({pickerOpen: true})}}>
                        <Translation>{this.props.isKioskMode ? "checkout__add_to_order" : "checkout__get_tickets"}</Translation>
                    </Button>
                </div>
            );
        }

        /**
         * Already carted items for the upsell, just show the success banner
         */
        // if (this.hasCartedItems()) {
        //     return (
        //         <AddedToCartMessage
        //             countCartedItems={+this.props.countCartedItems}
        //             canEdit={!!this.props.canEdit}
        //         />
        //     );
        // }

        /**
         * If there is a cart match, show the cart match offer
         */
        if (this.shouldShowCartMatch()) {
            return(
                <CartMatchOffer
                    details={this.getRelevantCartMatchDetails()}
                    priceLevels={this.isSeries() ? this.props.childEvent.price_levels : this.props.priceLevels}
                    onChangeTickets={this.changeTicketsFromCartMatchOffer}
                    onAccept={this.acceptCartMatchOffer}
                    disabled={!!this.props.loading}
                    loading={!!this.props.thisLoading}
                    isKioskMode={this.props.isKioskMode}
                />
            );
        }

        return null;
    }

    getTicketPickerOrDateTimePicker = () => {
        const isSeries = this.isSeries()
        /**
         * If the picker is opened:
         * For single event, show the ticket picker
         * For series event, show the ticket picker if there is a child event selected
         */
        if (!isSeries || this.canDisplaySeriesInventoryPicker()) {
            return (
                <Fragment>
                    <div className="upsell-embedded__ticket-picker">
                        <UpsellInventoryPicker
                            reducerKey={this.props.reducerKey}
                            priceLevels={isSeries ? this.props.childEvent.price_levels : this.props.priceLevels}
                            products={isSeries ? [] : this.props.products}
                            bundles={isSeries ? [] :this.props.bundles}
                            disabled={!!this.props.disabled}
                        />
                        {!!this.props.displayConfigurationError && <InventoryConfigurationErrors
                            productsWithErrors={this.getProductsWithErrors()}
                            bundleProductsWithErrors={this.getBundleProductsWithErrors()}
                            isKioskMode={this.props.isKioskMode}
                        />}
                    </div>
                    <div className="upsell-embedded__action">
                        <AddToOrderButton
                            onClick={this.submitPicker}
                            loading={this.props.thisLoading}
                            disabled={(!this.getNumberSelectedItems() && !this.getNumberSelectedProducts()) || !!this.props.loading || !!this.props.disabled}
                        />
                    </div>
                </Fragment>
            );
        }

        /**
         * If picker is opened for series, but no child event is selected
         * show the date time picker
         */
        return (
            <UpsellDateTimePicker
                event={this.props.event}
                reducerKey={this.props.reducerKey}
                upsellId={this.props.upsellId}
                onLoadChildEvent={this.loadChildEvent}
                loadingChildEvent={this.state.loadingChildEvent}
                isKiosk={this.isKiosk()}
            />
        );
    }

    /**
     * Is this a series upsell
     * @return bool
     */
    isSeries = () => {
        return !!this.props.event.isRecurring;
    }

    /**
     * Should the ticket picker be shown for series upsell
     * @return bool
     */
    canDisplaySeriesInventoryPicker = () => {
        return this.isSeries() && this.hasSelectedChildEvent() && this.state.showChildEventPicker;
    }

    getNumberSelectedItems = () => {
        return getTotalSelectedItemsFromState(this.props.ticketQuantitiesArray, this.props.bundleQuantitiesArray);
    }

    getNumberSelectedProducts = () => {
        return getTotalSelectedProductsFromState(this.props.productQuantitiesArray);
    }

    getProductsWithErrors = () => {
        return getProductErrorsFromState(this.props.productQuantities, this.props.productConfigQuantities, this.props.productsList);
    }

    getBundleProductsWithErrors = () => {
        return getBundleErrorsFromState(this.props.bundlesList, this.props.bundleQuantities, this.props.selectedBundleConfigs);
    }

    hasValidTimeslotMatch = () => {
        return !!this.props?.timeslotMatch?.event_id;
    }

    matchedTimeslotSelected = () => {
        return this.hasValidTimeslotMatch() && this.hasSelectedChildEvent() && this.props.timeslotMatch.event_id == this.props.childEvent.event_id;
    }

    hideChangeDateButton = () => {
        return this.hasValidTimeslotMatch() && !this.props.canChangeMatchedTimeslot;
    }

    /**
     * Load a series child event
     * If this upsell supports cart matching, check for cart match
     * If cart match found, save it in local state
     */
    loadChildEvent = async eventId => {
        this.setState({loadingChildEvent: true, childEventCartMatch: null, showCartMatch: true});
        let newCartMatch = null;
        try {
            await this.props.getEventSeriesChildListing(eventId, this.props.reducerKey, this.props.upsellId);

            if (this.props.doesCartMatching) {
                /**
                 * If this does cart matching AND timeslot matching, the cart match should already be loaded with the upsell content.
                 * Otherwise, we need to send a request to
                 * You might wonder "Why the inconsistency?" Well, if the upsell
                 * does timeslot matching, then we know what event to perform the cart matching
                 * on when we initially grab the upsell content. If not, then we have to wait until the user
                 * selects a child event to attempt cart matching.
                 */
                if (!this.matchedTimeslotSelected()) {
                    const match = await this.props.checkCartMatch(eventId, this.props.upsellId);
                    if (match) {
                        newCartMatch = match;
                    }
                }

            }
        } catch(err) {
            console.error(err);
        }
        this.setState({loadingChildEvent: false, showChildEventPicker: true, childEventCartMatch: newCartMatch});
    }

    onChangeDate = () => {
        this.props.setSelectedDate(null, this.props.reducerKey);
        this.props.clearChildEventDetails(this.props.reducerKey);
        this.props.changeDateInternal();
        this.setState({showChildEventPicker: false});
        if (this.props?.timeslotMatch) {
            this.setState({pickerOpen: true});
        }
    }

    getSelectedChildDisplayDate = () => {
        const start = this.props.childEvent?.event?.start;
        const end = this.props.childEvent?.event?.end;
        if (!start) {
            return "";
        }
        return formatDisplayDateForChangeDateButton(start, end, this.props.locale);
    }

    hasSelectedChildEvent = () => {
        return typeof this.props.childEvent === 'object' && this.props.childEvent !== null && !!Object.keys(this.props.childEvent).length;
    }

    showSelectedChildEventDate = () => {
        return this.isSeries() && this.canDisplaySeriesInventoryPicker() && this.props.countCartedItems < 1;
    }

    renderEvent = (underDescription = null) => {

        let imageSize = this.props.imageSize === IMAGE_SIZE_LARGE ? Event.size.large : Event.size.small;
        if (this.isKiosk() && this.props.imageSize === IMAGE_SIZE_LARGE) {
            imageSize = Event.size.largeLeft;
        }

        return (
            <Event
                size={imageSize}
                image={this.props.event.image}
                name={this.props.event.name}
                ages={this.props.event.age_minimum}
                hideAgeLimit={this.props.event.hide_age_limit}
                date={getEventDate(this.props.event.start, this.props.event.end, this.props.locale, this.props.event.hide_event_time, this.props.event.hide_event_date_and_time)}
                venue={this.getVenue()}
                isSeries={this.isSeries()}
                description={this.props.event.description}
                collapseDescription={!!this.props.collapseDescription}
                hideDate={this.showSelectedChildEventDate() || (this.isSeries() && this.props.countCartedItems > 0)}
                imageOnLeft={this.isKiosk()}
                underDescription={underDescription}
            />
        );
    }

    renderOnline() {

        const ui = this.shouldGetUI() ? this.getUI() : this.getTicketPickerOrDateTimePicker();

        return(
            <div className={this.props.position === "embedded_in_registration" ? 'upsell--embedded' : 'upsell-popup__content'}>
                {this.renderEvent()}
                {this.showSelectedChildEventDate() && (
                    <div className="upsell-embedded__ticket-picker">
                        <ChangeDate
                            onClick={this.onChangeDate}
                            displayDate={this.getSelectedChildDisplayDate()}
                            hideChangeButton={this.hideChangeDateButton()}
                        />
                    </div>
                )}
                {ui}
            </div>
        )
    }

    renderKiosk = () => {
        const shouldShowGetTicketsButton = this.shouldShowGetTicketsButton();
        const ui = this.shouldGetUI() ? this.getUI() : this.getTicketPickerOrDateTimePicker();
        const hasSelectedChildEvent = this.isSeries() && this.hasSelectedChildEvent();
        const displayDate = hasSelectedChildEvent ? this.getSelectedChildDisplayDate() : null;

        const underDescription = (
            <Fragment>
                {hasSelectedChildEvent && (
                    <div className='ticket__name'>
                        <b>{displayDate}</b>
                        {!this.hideChangeDateButton() && <ChangeDate.Button
                            displayDate={displayDate}
                            onClick={this.onChangeDate}
                        />}
                    </div>
                )}
            </Fragment>
        );

        return (
            <div className={this.props.position === "embedded_in_registration" ? 'upsell--embedded' : 'upsell-popup__content'}>
                <div className={`kiosk-upsell__ticket ${(!!this.props.event.image && this.props.imageSize) ? 'kiosk-upsell-image--' + this.props.imageSize : ''}`}>
                    <div className="kiosk-upsell__ticket__content">
                        <div className="kiosk-upsell__ticket__event">
                            {this.renderEvent(underDescription)}
                            {!shouldShowGetTicketsButton && ui}
                        </div>
                        <div className="kiosk-upsell__ticket__action">
                            {!this.hasCartedItems() ?
                                <>
                                    {/*Cart match price component*/}
                                    {this.shouldShowCartMatch() && (
                                        <CartMatchPrice
                                            details={this.getRelevantCartMatchDetails()}
                                            priceLevels={this.isSeries() ? this.props.childEvent.price_levels : this.props.priceLevels}
                                        />
                                    )}
                                    
                                    {/*Add-to-order button that submits a cart match*/}
                                    {this.shouldShowCartMatch() && (
                                        <AddToOrderButton
                                            onClick={this.acceptCartMatchOffer}
                                            loading={!!this.props.thisLoading}
                                            disabled={!!this.props.loading}
                                        />
                                    )}
                                </>
                                :
                                <>
                                    {/*Added To Cart message Component*/}
                                    <AddedToCartMessage
                                        countCartedItems={+this.props.countCartedItems}
                                        canEdit={!!this.props.canEdit}
                                    />
                                </>
                            }

                            {/* Non Cart Match ADD to Cart Button */}
                            {!!shouldShowGetTicketsButton && ui}
                        </div>
                    </div>
                    {/* <div className='kiosk-upsell__ticket__footer'}
                        {/* Move Here: Ticket Picker after "Change Tickets" clicked */}
                        {/* Move Here: Date Time Picker  */}
                    {/* </div> */}
                </div>
            </div>
        );
    }
}

const getReservationLoading = createLoadingSelector(["RESERVATION_CREATE", "RESERVATION_FETCH"]);
const mapStateToProps = (state, props) => {

    const ticketPickerState  = getSubState(state.ticketPicker, props.reducerKey);
    const bundlePickerState  = getSubState(state.bundlePicker, props.reducerKey);
    const productState       = getFetchedEventProducts(state);
    const productPickerState = getSubState(state.productPicker, props.reducerKey);
    const bundleState        = getFetchedEventBundles(state, props.reducerKey);
    const eventState         = getSubState(state.event, props.reducerKey);

    const getThisLoading = createLoadingSelector([getLoadingKeyFromProps(props)]);

    const isSeries = !!props?.event?.isRecurring;

    return {
        locale: state.intl.locale,
        loading: getReservationLoading(state), // any reservation loading
        thisLoading: getThisLoading(state), // a request for this particular upsell loading
        ticketQuantitiesArray: Object.values(ticketPickerState?.quantities ?? {}),
        bundleQuantitiesArray: Object.values(bundlePickerState?.bundleQuantities ?? {}),
        productsList: productState.products,
        productQuantities: productPickerState.productsQuantities ?? {},
        productConfigQuantities: productPickerState.configQuantities ?? {},
        productQuantitiesArray: Object.values(productPickerState.productsQuantities ?? {}),
        bundlesList: bundleState.bundles ?? {},
        bundleQuantities: bundlePickerState?.bundleQuantities ?? {},
        selectedBundleConfigs: bundlePickerState?.selectedConfigurations ?? {},
        displayConfigurationError: !!ticketPickerState?.displayConfigurationError,
        isKioskMode: isKioskMode(state),
        countCartedItems: countCartedItemsForUpsell(props.upsellId, props?.event?.event_id, isSeries)(state),
        childEvent: eventState.childEvent,
    }
}

const mapDispatchToProps = dispatch => {
    return bindActionCreators({
        setDisplayConfigurationError,
        updateQuantities,
        getEventSeriesChildListing,
        clearChildEventDetails,
        setSelectedDate,
        setPriceLevel,
        checkCartMatch,
        changeDateInternal,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EventUpsell);
